import { Form, FormApi, FormError, Option } from "informed";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { ServiceResourceCreation, ServiceResourceMutation, WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { TextBox } from "../../forms/informedv2/TextBox";
import { nameof } from "ts-simple-nameof";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import { TextArea } from "../../forms/informedv2/TextArea";
import { FileRejection } from 'react-dropzone';
import { DragDrop } from "../../misc/DragDrop";
import { UploadFile, CancellationState } from "../../misc/Hooks/UseFileUploads";
import { UploadProgressDialog } from "../../misc/Hooks/UseUploadProgressDialog";
import { AuditModal } from "../../auditmodal/components/AuditModal";
import { v4 } from "uuid";
import { DeviceType } from "../../misc/api/apiTypes";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { RequiredNotBlank } from "../../forms/validation/Required";
interface ExpectedMatchParams {
    serviceResourceId: string;
    state: "create" | "edit";
}

export interface ServiceResourceFormModel {
    name: string;
    serviceType: string;
    softwareType: string;
    subType: string;
    resourceVersion: number;
    softwareVersion: string;
    notes: string;
}

export const ServiceResourceCreateEditScreen = () => {
    const fetchServiceResource = async (): Promise<ServiceResourceCreation> => await client.client.getServiceResource(serviceResourceId);
    const [ cancellationState, setCancellationState ] = React.useState<CancellationState>(new CancellationState());
    const [existingDeviceFirmware, setExistingDeviceFirmware] = React.useState<ServiceResourceCreation>();
    const [currentServiceResource, setCurrentServiceResource] = React.useState<ServiceResourceCreation>(null);
    const [ currentServiceResourceId, setCurrentServiceResourceId ] = React.useState<string>(null);
     const [ initialValues, setInitialValues ] = React.useState<ServiceResourceFormModel>(null);
    const [ currentFileDetial, setCurrentFileDetail ] = React.useState<string>();
    const [ showAuditModal, setShowAuditModal ] = React.useState<boolean>(false);
    const client = useWebPortalApiClient();
    const [ createEditText, setCreateEditText ] = React.useState("Create");
    const [ successAlert, setSuccessAlert ] = React.useState<boolean>(false);
    const [ uploadProgress, setUploadProgress ] = React.useState<number>(0);
    const { serviceResourceId, state } = useParams<ExpectedMatchParams>();
    const [ errorAlert, setErrorAlert ] = React.useState<boolean>(false);
    const [ currentFile, setCurrentFile ] = React.useState<File>(null);
    const [ editState, setEditState ] = React.useState<boolean>(false);
    const [ uploading, setUploading ] = React.useState<boolean>(false);
    const [ annotation, setAnnotation ] = React.useState<string>("");
    const [ errorText, setErrorText ] = React.useState<string>("");
    const [isBusy, setIsBusy] = React.useState<boolean>(false);
    const [fileUploadRequired, setFileUploadRequired] = React.useState<boolean>(false);
    const { study, deviceTypes } = useCurrentStudyConfigurationContext();

   
    const softwareTypes = [
        { name: "Firmware" },
        { name: "Report" },
        { name: "String" },
        { name: "Rules" },
        { name: "Manifest" },
        { name: "Package" },
        { name: "Script" }];

    /* Templates */
    const tooltip = (text: string) => {
        return (<Tooltip placement="top" className="in" id="tooltip-top">
            {text}
        </Tooltip>);
    },  overlayTrigger = (text: string) => {
        return (<OverlayTrigger placement="top" overlay={tooltip(text)}>
            <i className="fa fa-info-circle text-muted" />
        </OverlayTrigger>);}

    React.useEffect(() => {
        SpinNewResourceId();
    }, []);

    React.useEffect(() => {
        if (isEdit()) {
            setEditState(true);
            setCreateEditText("Edit");
            fetchServiceResource().then(setCurrentServiceResource);
        }

        InitializeFormFields(deviceTypes);

    }, [state]);

    const isEdit = (): boolean => state == "edit";

    const SpinNewResourceId = () => setCurrentServiceResourceId(v4());

    const InitializeFormFields = async (deviceTypes: DeviceType[]) => {
        let values: ServiceResourceFormModel = null;
        let initialDeviceType: string = "";
        let initialSoftwareType: string = "";

        if (!isEdit()) {
            initialDeviceType = deviceTypes[0].Name;
            initialSoftwareType = softwareTypes[0].name;

            values = {
                name: "",
                notes: "",
                resourceVersion: 1,
                softwareVersion: "",
                subType: "",
                serviceType: initialDeviceType,
                softwareType: initialSoftwareType,
            }
            setCurrentFileDetail("");
        } else {
            const existing = await FetchAndSetExistingDeviceFirmware();
            values = {
                name: existing.name,
                notes: existing.description,
                resourceVersion: existing.revisionNumber,
                softwareVersion: existing.softwareVersion,
                subType: existing.subType,
                serviceType: existing.deviceType,
                softwareType: existing.softwareType,
            }
            initialDeviceType = existing.deviceType;
            setCurrentFileDetail(existing.softwareFileName);
        }
        setInitialValues(values);
    }

    const EmptyInitialValues = (): ServiceResourceFormModel => {
        return {
            name: "",
            notes: "",
            resourceVersion: 1,
            softwareVersion: "",
            subType: "",
            serviceType: deviceTypes[0].Name,
            softwareType: softwareTypes[0].name,
        }
    }

    const FetchAndSetExistingDeviceFirmware = async (): Promise<ServiceResourceCreation> => {
        const query = isEdit() ? `id eq ${serviceResourceId}` : `id eq ${currentServiceResourceId}`;
        const existingQuery = await client.client.getServiceResources(undefined, query, undefined);
        const existing = existingQuery.value[0];
        setExistingDeviceFirmware(existing);
        if (isEdit()) {
            setCurrentServiceResourceId(existing.id);
        }
        return existing;
    }

    const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
        setFileUploadRequired(false);
        setCurrentFileDetail(accepted[0].name);
        setCurrentFile(accepted[0]);
    };

    const performUpload = async (file: File): Promise<boolean> => {
        ResetUploadState();
        setUploading(true);

        const result = await UploadFile({
            client: client.client,
            uploadId: currentServiceResourceId,
            fileSource: file,
            reportProgress: percent => setUploadProgress(percent),
            cancellationState: cancellationState
        });

        ResetUploadState();
        return result;
    }

    const ResetUploadState = (resetCancellationState : boolean = true) => {
        setUploading(false);
        setUploadProgress(0);
        setCurrentFile(null);

        if(resetCancellationState)
            cancellationState.SetCancellationValue(false);
    }

    const uploadCancelled = () => {
        ResetUploadState(false);
        cancellationState.SetCancellationValue(true);
    }

    const handleSubmit = async (values: ServiceResourceFormModel) => {
        setSuccessAlert(false);

        if (formValidator(values)) {
            return;
        }

        if (editState) {
            setShowAuditModal(true);
        } else {
            await handleAuditSubmit(values);
        }
    }

    const GenerateFormCreation = (values: ServiceResourceFormModel): ServiceResourceCreation => {
        return {
            id: currentServiceResourceId,
            name: values.name,
            description: values.notes,
            softwareFileName: currentFile.name,
            softwareType: values.softwareType,
            deviceType: values.serviceType,
            subType: values.subType,
            softwareVersion: values.softwareVersion,
            studyId: study.id
        }
    }

    const GenerateFormMutation = (values: ServiceResourceFormModel, resourceFileUpdated: boolean): ServiceResourceMutation => {
        return {
            name: values.name,
            description: values.notes,
            softwareFileName: currentFile?.name ?? existingDeviceFirmware?.softwareFileName,
            softwareType: values.softwareType,
            deviceType: values.serviceType,
            subType: values.subType ?? null,
            softwareVersion: values.softwareVersion,
            revisionNumber: existingDeviceFirmware.revisionNumber + (resourceFileUpdated ? 1 : 0),
        }
    }

    const ShouldUploadFile = (): boolean => {
        if (!editState)
            return true;

        if (editState && currentFile != null)
            return true;

        return false;
    }

    const handlePostOrPatch = async (values: ServiceResourceFormModel, resourceFileUpdated: boolean) => {
        if (editState) {
            await client.client.patchServiceResource(GenerateFormMutation(values, resourceFileUpdated), existingDeviceFirmware.id, annotation);
        } else {
            await client.client.postServiceResource(GenerateFormCreation(values));
        }
    }

    const DisplayError = (error: string) => {
        setErrorAlert(true);
        setErrorText(error);
    }

    const FlushForm = () => {
        setInitialValues(EmptyInitialValues());
        SpinNewResourceId();
        setCurrentFileDetail("");
    }

    const handleAuditSubmit = async (values: ServiceResourceFormModel, formApi?: FormApi<ServiceResourceFormModel>) => {
        setIsBusy(true);
       
        try {
            let shouldUploadFile = ShouldUploadFile();
            if (shouldUploadFile)
            {
                setShowAuditModal(false);
                if ( ! await performUpload(currentFile))
                {
                    DisplayError("Error: Failed to upload the Resource File");
                    return;
                }
            }
            await handlePostOrPatch(values, shouldUploadFile && editState);
            if (editState) {
                const updatedDeviceFirmware = await FetchAndSetExistingDeviceFirmware();

                if (formApi != null) {
                    formApi.setValue("resourceVersion", updatedDeviceFirmware.revisionNumber);
                }
                
            } else {
                FlushForm();
            }
            setSuccessAlert(true);
            setErrorAlert(false);
        } catch (exception) {
            DisplayError(exception.message);
        } finally {
            setIsBusy(false);
            setShowAuditModal(false);
        } 
    }

    const HandleClose = () => setShowAuditModal(false);

    const formLoading = () : boolean => {
        if (!initialValues)
            return true;

        if (!serviceResourceId && editState)
            return true;

        if (!editState && currentServiceResourceId === null)
            return true;

        if ((currentServiceResource === null && editState))
            return true;

        return false;
    }
    
    if (formLoading())
        return (<LoadingWithMessage message="Loading..." />);

    const formValidator = (values: ServiceResourceFormModel): FormError => {
        setFileUploadRequired(false);

        if (currentFile == null && !editState) {
            setFileUploadRequired(true);
            return "Resource File required";
        }

        if (!values.name)
            return "Name cannot be empty";

        if (!values.name.trim())
            return "Name cannot be empty or only contain spaces";

        if (!values.softwareVersion)
            return "Software Version must be specified";

        if (!values.softwareVersion.trim())
            return "Software Version cannot be empty or only contain spaces";

        return null;
    }

    return (<>
        <div className="wrapper wrapper-content">
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/serviceresources`}>Service Resource list</Link>?
            </Alert>
            <Alert show={errorAlert} category={AlertCategoryEnum.Danger}>
                {errorText}
            </Alert>
            <Form<ServiceResourceFormModel> id="serviceResourceForm" onSubmit={handleSubmit} initialValues={initialValues} validate={formValidator} >
                {({ formState, formApi }) =>
                (<div className="row">
                    <Alert show={formState.error} category={AlertCategoryEnum.Danger}>
                        {formState.error}
                    </Alert>
                 
                    <div className="col-md-12">
                        <div className="ibox">
                            <div className="ibox-title">
                                <div className="row">
                                    <div className="col-md-9">
                                        <h2 className="text-primary">{createEditText} Service Resource For Study {study.name}</h2>
                                    </div>

                                    <div className="col-md-3">
                                        <div className="pull-right">
                                            <button onClick={(e) => { e.preventDefault(); formApi.validate(); handleSubmit(formState.values); }} className="btn btn-primary right">Save</button>
                                            <Link id="cancelForm" to="/serviceresources" className="btn btn-primary btn-outline right">Cancel</Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ibox-content">
                                <div className="row">
                                    <div className="form-group col-md-3 col-sm-3 col-xs-12">
                                        <label htmlFor="name">Name</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("Name of the service resource")}
                                        <TextBox field="name" validate={RequiredNotBlank} />
                                    </div>
                                    <div className="form-group col-md-3 col-sm-3 col-xs-12">
                                        <label htmlFor="serviceType">Service Type</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("Type of service to which this resource item applies")}
                                        <Dropdown id="serviceType" field={nameof<ServiceResourceFormModel>(f => f.serviceType)} value={deviceTypes?.slice(0, 1).map(dt => dt.Name)} >
                                            {deviceTypes?.map(dt => <Option id={dt.Name} key={dt.Name}>{dt.Name}</Option>)}
                                        </Dropdown>
                                    </div>

                                    <div className="form-group col-md-3 col-sm-3 col-xs-12">
                                        <label htmlFor="softwareType">Software Type</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("Type of software to which this resource item applies")}
                                        <Dropdown id="softwareType" field={nameof<ServiceResourceFormModel>(f => f.softwareType)} >
                                            {softwareTypes.map(st => <Option key={st.name} value={st.name} >{st.name}</Option>)}
                                        </Dropdown>
                                    </div>
                                    <div className="form-group col-md-3 col-sm-3 col-xs-12">
                                        <label>Sub Type</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("The field Sub Type must be a string with a maximum length of 100")}
                                        <TextBox id="subType" field="subType" />
                                    </div>
                                </div>

                                <div className="row">
                      
                                  
                                    <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                        <label>Resource File</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("Source file for the resource")}
                                        <UploadProgressDialog when={uploading} currentProgress={uploadProgress} onCancel={uploadCancelled} />
                                        <div className="panel panel-default">
                                            <div className="panel-body" style={{ paddingBottom: currentFileDetial ? 1 : 15 }}>
                                                <DragDrop
                                                    id="file"
                                                    options={{
                                                        onDrop,
                                                        maxFiles: 1,
                                                        maxSize: 24000000000,
                                                    }}
                                                    isError={fileUploadRequired}
                                                />
                                                <label style={{ top: -8 }} className="pull-right">{currentFileDetial}</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group col-md-3 col-sm-3 col-xs-12">
                                        <label>Notes</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("Notes for the resource")}
                                        <TextArea style={{ height: 109 }} id="notes" field="notes"></TextArea>
                                    </div>
                                    <div className="form-group col-md-3 col-sm-3 col-xs-12">
                                        <label>Software Version</label>
                                        &nbsp;&nbsp;
                                        {overlayTrigger("Version of the firmware")}
                                        <TextBox id="softwareVersion" min="0" field="softwareVersion" validate={RequiredNotBlank} />
                                    </div>
                                </div>
                            </div>

                            <div className="ibox-footer">
                            </div>
                        </div>
                    </div>
                    {showAuditModal ? <AuditModal onClose={HandleClose} isSubmitting={isBusy}
                        onSubmit={v => { setAnnotation(v.Annotation); handleAuditSubmit(formState.values, formApi) }} header="Service Resource Edit" /> : null}
                </div>)
                }
            </Form>
        </div>
     </>);
}
